/**
 * Chat Transport Interface - AI SDK v6 compatible.
 *
 * Provides a pluggable transport abstraction for chat streaming,
 * following the Vercel AI SDK transport pattern.
 */

import type { UIMessage, DebugEntry } from '../types/chat';

/**
 * Stream event from the transport layer.
 * Follows AI SDK v6 event format.
 */
export interface StreamEvent {
  type: string;
  [key: string]: unknown;
}

/**
 * Request options for sending messages.
 */
export interface SendMessagesRequest {
  /** Unique chat/session identifier */
  chatId: string;
  /** User identifier */
  userId: string;
  /** Optional organization identifier */
  organizationId?: string;
  /** Message text to send */
  message: string;
  /** Trigger source */
  trigger: 'submit-message' | 'regenerate-message' | 'retry-message';
  /** Optional abort signal for cancellation */
  abortSignal?: AbortSignal;
  /** Optional additional headers */
  headers?: Record<string, string>;
  /** Optional additional body fields */
  body?: Record<string, unknown>;
}

/**
 * Options for reconnecting to an existing stream.
 */
export interface ReconnectRequest {
  /** Chat/session identifier to reconnect to */
  chatId: string;
  /** User identifier */
  userId: string;
  /** Optional abort signal */
  abortSignal?: AbortSignal;
}

/**
 * Tool output submission request.
 */
export interface ToolOutputRequest {
  /** Chat/session identifier */
  chatId: string;
  /** User identifier */
  userId: string;
  /** Tool call identifier */
  toolCallId: string;
  /** Tool execution output */
  output: unknown;
  /** Whether the tool execution errored */
  isError?: boolean;
}

/**
 * Transport configuration options.
 */
export interface ChatTransportConfig {
  /** Base URL for the agent endpoint */
  baseUrl: string;
  /** Optional default headers */
  headers?: Record<string, string> | (() => Record<string, string>);
  /** Optional credentials mode */
  credentials?: RequestCredentials;
  /** Optional callback for debug logging */
  onDebugEntry?: (entry: Omit<DebugEntry, 'id' | 'timestamp'>) => void;
}

/**
 * Transport connection state.
 */
export type TransportConnectionState =
  | 'disconnected'
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'error';

/**
 * Chat transport interface.
 *
 * Defines the contract for transport implementations that handle
 * communication between the frontend and agent backend.
 */
export interface ChatTransport {
  /**
   * Send messages and return an async iterable of stream events.
   */
  sendMessages(request: SendMessagesRequest): AsyncIterable<StreamEvent>;

  /**
   * Attempt to reconnect to an existing stream.
   * Returns null if reconnection is not supported or fails.
   */
  reconnectToStream(request: ReconnectRequest): Promise<AsyncIterable<StreamEvent> | null>;

  /**
   * Stop the current stream.
   */
  stop(chatId: string, userId: string): Promise<void>;

  /**
   * Submit tool execution output back to the agent.
   * Optional - not all transports support bidirectional tool communication.
   */
  addToolOutput?(request: ToolOutputRequest): Promise<void>;

  /**
   * Get the current connection state.
   * Optional - only relevant for persistent connection transports like WebSocket.
   */
  getConnectionState?(): TransportConnectionState;

  /**
   * Clean up resources.
   */
  dispose(): void;
}

/**
 * Helper to resolve headers from config.
 */
export function resolveHeaders(
  config: ChatTransportConfig,
  additional?: Record<string, string>,
): Record<string, string> {
  const base = typeof config.headers === 'function' ? config.headers() : config.headers || {};
  return { ...base, ...additional };
}
